
import { supabase } from "@/integrations/supabase/client";
import { WidgetDefinition } from "@/components/admin/dashboard/widget-types";
import { DEFAULT_USER_WIDGET_ORDER, WIDGET_DEFINITIONS } from "@/components/dashboard/utils";
import { DEFAULT_WIDGET_ORDER } from "@/components/admin/dashboard/constants";
import { fetchWidgetDefinitionsForDashboard } from "./widgets";
import { ADMIN_WIDGET_DEFINITIONS } from "@/components/admin/dashboard/admin-widgets";
import { USER_WIDGET_DEFINITIONS } from "@/components/admin/dashboard/user-widgets";

// Get all active widget definitions that should be available for a specific dashboard type
export const getActiveWidgetsForDashboard = async (dashboardType: 'admin' | 'user'): Promise<WidgetDefinition[]> => {
  try {
    console.log(`Getting active widgets for ${dashboardType} dashboard...`);
    // Fetch active widget definitions from the database
    const widgetDefinitions = await fetchWidgetDefinitionsForDashboard(dashboardType);
    
    // Log the retrieved widget definitions to help debugging
    console.log(`Retrieved ${widgetDefinitions.length} widgets for ${dashboardType} dashboard:`, 
      widgetDefinitions.map(w => ({ id: w.id, title: w.title, isActive: w.isActive })));
    
    // If we have widget definitions, filter and return the active ones
    if (widgetDefinitions && widgetDefinitions.length > 0) {
      console.log(`Found ${widgetDefinitions.length} widget definitions for ${dashboardType} dashboard, filtering active ones`);
      const activeWidgets = widgetDefinitions.filter(widget => widget.isActive);
      console.log(`Active widgets for ${dashboardType} dashboard:`, 
        activeWidgets.map(w => ({ id: w.id, title: w.title })));
      return activeWidgets;
    }
    
    console.log(`No widget definitions found in DB for ${dashboardType} dashboard, falling back to static definitions`);
    // If no widget definitions in DB, fall back to static definitions
    return getStaticWidgetsForDashboard(dashboardType);
  } catch (error) {
    console.error(`Error in getActiveWidgetsForDashboard for ${dashboardType}:`, error);
    // Fall back to static definitions if there's an error
    console.log(`Falling back to static widget definitions for ${dashboardType} dashboard due to error`);
    return getStaticWidgetsForDashboard(dashboardType);
  }
};

// Fallback to static widget definitions if the database query fails
const getStaticWidgetsForDashboard = (dashboardType: 'admin' | 'user'): WidgetDefinition[] => {
  // Get the appropriate static widget definitions based on dashboard type
  console.log(`Using static widget definitions for ${dashboardType} dashboard`);
  const staticWidgets = dashboardType === 'admin' 
    ? ADMIN_WIDGET_DEFINITIONS 
    : USER_WIDGET_DEFINITIONS;
  
  console.log(`Found ${staticWidgets.length} static widget definitions for ${dashboardType} dashboard:`, 
    staticWidgets.map(w => ({ id: w.id, title: w.title, isActive: w.isActive })));
  
  // Make sure to include the financial-result widget for user dashboard
  if (dashboardType === 'user') {
    const hasFinancialResult = staticWidgets.some(widget => widget.id === 'financial-result');
    if (!hasFinancialResult) {
      console.warn("Financial Result widget not found in static widget definitions, this might be a problem");
    } else {
      console.log("Financial Result widget found in static widget definitions");
    }
  }
  
  // Filter by active widgets and return
  const activeWidgets = staticWidgets.filter(widget => widget.isActive);
  console.log(`Active static widgets for ${dashboardType} dashboard:`, 
    activeWidgets.map(w => ({ id: w.id, title: w.title })));
  return activeWidgets;
};

// Get the default widget layout for a dashboard type
export const getDefaultWidgetLayout = (dashboardType: 'admin' | 'user') => {
  return dashboardType === 'admin' ? DEFAULT_WIDGET_ORDER : DEFAULT_USER_WIDGET_ORDER;
};

// Filter widget layout items based on active widgets
export const filterActiveWidgets = async (layoutItems: any[], dashboardType: 'admin' | 'user') => {
  const activeWidgets = await getActiveWidgetsForDashboard(dashboardType);
  const activeWidgetIds = activeWidgets.map(widget => widget.id);
  
  return layoutItems.filter(item => activeWidgetIds.includes(item.id));
};
